import { useEffect, useState } from "react";
import { api } from "../lib/api";

const abs = (u) => (u || "").startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u;

/** Rotating admin-created ads — shown to every user on the dashboard. */
export default function AdsRotator({ fitCreative = false }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => { api.get("/ads/active").then((r) => setAds(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (ads.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(t);
  }, [ads.length]);

  if (!ads.length) return null;
  const ad = ads[idx];

  const click = () => {
    api.post(`/admin/ads/${ad.id}/click`).catch(() => {});
    if (ad.target_url) {
      const url = new URL(ad.target_url, window.location.origin);
      if (["http:", "https:"].includes(url.protocol)) window.open(url.href, "_blank", "noopener,noreferrer");
    }
  };

  if (fitCreative) {
    const cleanTitle = typeof ad.title === "string" && /[A-Za-z0-9]{2,}/.test(ad.title.trim()) ? ad.title.trim() : "";
    return (
      <div onClick={click} role="button" tabIndex={0} aria-label={`Advertisement${cleanTitle ? `: ${cleanTitle}` : ""}`} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); click(); } }} data-testid="ads-rotator"
           className="overflow-hidden rounded-2xl border border-[#DDE8E1] bg-white shadow-[0_6px_18px_rgba(24,61,67,.08)] cursor-pointer group">
        <div className="relative aspect-video w-full overflow-hidden bg-[#EAF5EF]">
          {ad.media_type === "video"
            ? <video src={abs(ad.media_url)} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-contain" />
            : <img src={abs(ad.media_url)} alt={cleanTitle || "Sponsored promotion"} className="absolute inset-0 h-full w-full object-contain" />}
        </div>
        <div className="flex items-start justify-between gap-3 px-3.5 py-3">
          <div className="min-w-0">
            <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#147D60]">Sponsored</div>
            {cleanTitle && <div className="mt-0.5 truncate text-sm font-bold text-[#1A303B]">{cleanTitle}</div>}
            {ad.description && <div className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-[#60727A]">{ad.description}</div>}
            {ad.target_url && <span className="mt-1 inline-block text-[11px] font-bold text-[#147D60]">Learn more →</span>}
          </div>
          {ads.length > 1 && (
            <div className="mt-1 flex shrink-0 gap-1" aria-label={`Ad ${idx + 1} of ${ads.length}`}>
              {ads.map((_, i) => <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-[#147D60]" : "bg-[#147D60]/25"}`} />)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onClick={click} role="button" tabIndex={0} aria-label={`Advertisement: ${ad.title}`} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();click();}}} data-testid="ads-rotator"
         className="relative overflow-hidden rounded-2xl border border-[#26263A] cursor-pointer group">
      {ad.media_type === "video"
        ? <video src={abs(ad.media_url)} autoPlay muted loop playsInline className="w-full max-h-56 object-cover" />
        : <img src={abs(ad.media_url)} alt={ad.title} className="w-full max-h-56 object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-2.5">
        <span className="text-[8px] uppercase tracking-[0.3em] font-black text-[#F59E0B]">Sponsored</span>
        <div className="text-sm font-bold text-white truncate">{ad.title}</div>
        {ad.description && <div className="text-[10px] text-white/70 truncate">{ad.description}</div>}
      </div>
      {ads.length > 1 && (
        <div className="absolute top-2 right-2 flex gap-1">
          {ads.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`} />)}
        </div>
      )}
    </div>
  );
}
